import { useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Generic drag-and-drop Kanban board.
 * props:
 *  - columns: [{ key, label, cls }]
 *  - items: array of objects
 *  - getStatus: (item) => columnKey
 *  - getId: (item) => id
 *  - onMove: (id, newColumnKey) => void
 *  - renderCard: (item) => ReactNode
 */
export function KanbanBoard({ columns, items, getStatus, getId, onMove, renderCard }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const grouped = columns.reduce((acc, c) => {
    acc[c.key] = items.filter(i => getStatus(i) === c.key);
    return acc;
  }, {});

  const handleDrop = (colKey) => {
    if (dragId != null) {
      const item = items.find(i => getId(i) === dragId);
      if (item && getStatus(item) !== colKey) onMove(dragId, colKey);
    }
    setDragId(null);
    setOverCol(null);
  };

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="kanban-board">
      {columns.map(col => (
        <div
          key={col.key}
          onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
          onDragLeave={() => setOverCol(prev => (prev === col.key ? null : prev))}
          onDrop={() => handleDrop(col.key)}
          className={cn(
            'rounded-xl border bg-muted/30 p-2.5 flex flex-col min-h-[120px] transition-colors',
            overCol === col.key && 'ring-2 ring-primary/50 bg-primary/5'
          )}
          data-testid={`kanban-col-${col.key}`}
        >
          <div className="flex items-center justify-between px-1.5 pb-2 mb-1 border-b">
            <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full border', col.cls)}>{col.label}</span>
            <span className="text-xs text-muted-foreground tabular-nums">{grouped[col.key].length}</span>
          </div>
          <div className="space-y-2 flex-1">
            {grouped[col.key].length === 0 ? (
              <p className="text-[11px] text-muted-foreground text-center py-6">Drop here</p>
            ) : grouped[col.key].map(item => (
              <div
                key={getId(item)}
                draggable
                onDragStart={() => setDragId(getId(item))}
                onDragEnd={() => { setDragId(null); setOverCol(null); }}
                className={cn('cursor-grab active:cursor-grabbing', dragId === getId(item) && 'opacity-50')}
                data-testid={`kanban-card-${getId(item)}`}
              >
                {renderCard(item)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
